/**
 * Factory Classification — the persisted verdict of the factory's classifier.
 *
 * One shape, written in two places: the `assessment` record on the run (U1's
 * backend `AssessmentRecord` embeds it) and the payload of every
 * `run.classified` event (U2). A human override writes the same shape with
 * `source: 'override'`. Keeping it here, once, is what makes precision per
 * field computable from the ledger: `sj runs classifications` compares the
 * model's `suggestedLane`, `sizeClass` and `riskFlags` against the override and
 * the outcome without a per-writer adapter.
 *
 * THE CLASSIFIER PROPOSES; IT NEVER DECIDES SPEND. `lane` is the lane the run
 * will dispatch on, and it is set only by an authoritative source — a label
 * rule, a resume comment naming the lane, or an override. `suggestedLane` is the
 * model's guess, and under `factory.assessment.unlabeledPolicy: park` a run
 * whose lane came only from the model has `lane: null` and parks with the
 * suggestion as its question (KD6, KTD16).
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-09-20-1756-feat-software-factory-phase-runner-plan.md
 */

import { z } from 'zod'

import { AgentRunLaneVerdictSchema, RiskFlagsSchema, SizeClassSchema } from './agent-run'

/** The assessment providers (KTD16) the classifier eval compares. */
export const FACTORY_CLASSIFIER_PROVIDERS = ['openrouter', 'typesafe'] as const

/**
 * What kind of request a candidate is, independent of which lane serves it.
 *
 * Broader than the lanes on purpose: `investigation` and `docs` have no lane
 * today and route by policy (park or decline), and `unknown` is the honest
 * answer when neither rules nor the model can say. Minting a lane from a kind is
 * out of scope for the phase runner plan.
 */
export const FACTORY_REQUEST_KINDS = [
  'defect',
  'improvement',
  'feature',
  'investigation',
  'support',
  'ops',
  'security',
  'release',
  'docs',
  'unknown'
] as const

export const FactoryRequestKindSchema = z.enum(FACTORY_REQUEST_KINDS)
export type FactoryRequestKind = z.infer<typeof FactoryRequestKindSchema>

/** Who wrote a classification: the assessment job, or a human through the override route. */
export const FACTORY_CLASSIFICATION_SOURCES = ['assess', 'override'] as const

export const FactoryClassificationSourceSchema = z.enum(FACTORY_CLASSIFICATION_SOURCES)
export type FactoryClassificationSource = z.infer<typeof FactoryClassificationSourceSchema>

/**
 * The longest single `evidence[]` quote, in characters.
 *
 * A QUOTE, NOT A SUMMARY. Evidence is the span of ticket text that made the
 * classifier say what it said, so a reviewer can check it at a glance; 200
 * characters holds a sentence or two. The bound also keeps the `run.classified`
 * payload far below the recorder's 16,384-character truncation, where a stored
 * stub would replace the whole verdict.
 */
export const FACTORY_EVIDENCE_QUOTE_MAX = 200

/** At most this many evidence quotes per classification — the same size argument as the quote bound. */
export const FACTORY_EVIDENCE_MAX_ITEMS = 8

/** The fields alone, for a payload that adds its own (`run.classified` adds `findingId`). */
export const FactoryClassificationFieldsSchema = z.object({
  /** Which classifier produced this, e.g. `jev-1`. Bumped whenever rules, prompt or schema change. */
  classifierVersion: z.string().min(1).max(64),
  /** The OpenRouter model id that answered, or null when the rules decided alone. */
  modelId: z.string().min(1).nullable(),
  /** sha256 hex of the classifier's input, so two verdicts over the same text are recognisable. */
  inputDigest: z.string().regex(/^[0-9a-f]{64}$/, 'must be lowercase sha256 hex'),
  requestKind: FactoryRequestKindSchema,
  /** The lane the run dispatches on — authoritative sources only. Null when it parks or declines. */
  lane: AgentRunLaneVerdictSchema.nullable(),
  /** The classifier's own lane guess, whether or not it was acted on. */
  suggestedLane: AgentRunLaneVerdictSchema.nullable(),
  /** Null when the rules decided and no model was asked. */
  sizeClass: SizeClassSchema.nullable(),
  riskFlags: RiskFlagsSchema,
  evidence: z.array(z.string().min(1).max(FACTORY_EVIDENCE_QUOTE_MAX)).max(FACTORY_EVIDENCE_MAX_ITEMS),
  /** The model's confidence in `suggestedLane`; null when the rules decided alone. */
  confidence: z.number().min(0).max(1).nullable(),
  /** Rule ids in evaluation order, whether or not they decided. */
  rulesFired: z.array(z.string().min(1).max(64)).max(32),
  /** From the provider's `usage`; null when no model was called or it reported none. */
  costUsd: z.number().nonnegative().nullable(),
  /**
   * Token counts, as `{ input, output }`. NOT `inputTokens`/`outputTokens`:
   * the operations-event redactor blanks any key containing "token", which
   * would store `"[redacted]"` where this schema requires a number.
   */
  usage: z.object({ input: z.number().int().nonnegative(), output: z.number().int().nonnegative() }).nullable(),
  /** True when the primary model failed or timed out and the configured fallback answered. */
  usedFallbackModel: z.boolean(),
  source: FactoryClassificationSourceSchema,
  /**
   * 1 for the first assessment of a run, incremented by each re-assessment on
   * resume. Part of the `run.classified` dedupe key, so a second verdict is a
   * second event rather than a dedupe into the first (KTD24).
   */
  assessmentGeneration: z.number().int().min(1)
})

/**
 * No model answered ⇒ no suggestion, no model confidence and no fallback. Cost
 * stays independent — a model call that failed outright can still have spent —
 * and so do size, risk and evidence, which a rule or an override may supply.
 */
function modelFieldsAgree(c: {
  modelId: string | null
  suggestedLane: string | null
  confidence: number | null
  usedFallbackModel: boolean
}) {
  return c.modelId !== null || (c.suggestedLane === null && c.confidence === null && !c.usedFallbackModel)
}

const MODEL_FIELDS_MESSAGE =
  'suggestedLane, confidence and usedFallbackModel describe a model answer; with modelId null they must be null, null and false'

export const FactoryClassificationSchema = FactoryClassificationFieldsSchema.refine(modelFieldsAgree, {
  message: MODEL_FIELDS_MESSAGE,
  path: ['modelId']
})
export type FactoryClassification = z.infer<typeof FactoryClassificationSchema>

/**
 * `classified` (stored as `run.classified`) — one classification verdict,
 * written after assessment's status CAS has won, and again for each
 * re-assessment and each human override (KTD24).
 *
 * `findingId` is the ledger's dedupe key and is on the payload because the
 * ledger reads it from there: `${classifierVersion}:${assessmentGeneration}`
 * for an assessment, so a re-assessment on resume is a second event rather than
 * a dedupe into the first; an override names its own.
 *
 * Strict, unlike the third-party payloads in `agent-run-outcome-events.ts`:
 * it is written by our own code, and precision is computed from these rows.
 */
export const ClassifiedPayloadSchema = FactoryClassificationFieldsSchema.extend({
  findingId: z.string().min(1).max(200)
})
  .refine(modelFieldsAgree, { message: MODEL_FIELDS_MESSAGE, path: ['modelId'] })
  // An assessment's key is derived, so a later generation can never reuse an
  // earlier one's and dedupe into it; an override names its own.
  .refine(c => c.source !== 'assess' || c.findingId === `${c.classifierVersion}:${c.assessmentGeneration}`, {
    message: 'an assessment findingId must be `${classifierVersion}:${assessmentGeneration}`',
    path: ['findingId']
  })
export type ClassifiedPayload = z.infer<typeof ClassifiedPayloadSchema>
